import type { Factor } from "../types/driehoek-data";

export const SECTORS: Record<Factor, [number, number][]> = {
  omgeving: [
    [0, 0],
    [200, 0],
    [200, 82],
    [74, 301],
    [0, 301],
  ],
  gezin: [
    [400, 0],
    [200, 0],
    [200, 82],
    [326, 301],
    [400, 301],
  ],
  kind: [
    [0, 301],
    [400, 301],
    [400, 400],
    [0, 400],
  ],
};

const EDGES: Record<Factor, { start: [number, number]; end: [number, number] }> = {
  omgeving: { start: [200, 82], end: [74, 301] },
  gezin: { start: [200, 82], end: [326, 301] },
  kind: { start: [74, 301], end: [326, 301] },
};

function sign(p1: [number, number], p2: [number, number], p3: [number, number]) {
  return (p1[0] - p3[0]) * (p2[1] - p3[1]) - (p2[0] - p3[0]) * (p1[1] - p3[1]);
}

function pointInTriangle(x: number, y: number, tri: [number, number][]) {
  const [a, b, c] = tri;
  const d1 = sign([x, y], a, b);
  const d2 = sign([x, y], b, c);
  const d3 = sign([x, y], c, a);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

function pointInPolygon(x: number, y: number, poly: [number, number][]) {
  if (poly.length === 3) return pointInTriangle(x, y, poly);
  let inside = false;
  const n = poly.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (((yi > y) !== (yj > y)) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

export function whichSector(x: number, y: number): Factor | null {
  for (const [factor, poly] of Object.entries(SECTORS) as [Factor, [number, number][]][]) {
    if (pointInPolygon(x, y, poly)) return factor;
  }
  return null;
}

function nearestPointOnSegment(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  px: number,
  py: number
): [number, number] {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return [ax, ay];
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return [ax + t * dx, ay + t * dy];
}

export function sectorCentroid(factor: Factor): [number, number] {
  const poly = SECTORS[factor];
  let sx = 0;
  let sy = 0;
  for (const p of poly) {
    sx += p[0];
    sy += p[1];
  }
  return [sx / poly.length, sy / poly.length];
}

export function clampToSector(x: number, y: number, factor: Factor): [number, number] {
  const poly = SECTORS[factor];
  if (pointInPolygon(x, y, poly)) return [x, y];
  let best = sectorCentroid(factor);
  let bestDist = Infinity;
  const n = poly.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [ax, ay] = poly[j];
    const [bx, by] = poly[i];
    const [nx, ny] = nearestPointOnSegment(ax, ay, bx, by, x, y);
    const d = (nx - x) ** 2 + (ny - y) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = [nx, ny];
    }
  }
  return best;
}

function projectToEdge(factor: Factor, x: number, y: number) {
  const e = EDGES[factor];
  const dx = e.end[0] - e.start[0];
  const dy = e.end[1] - e.start[1];
  const len = Math.sqrt(dx * dx + dy * dy);
  const u = ((x - e.start[0]) * dx + (y - e.start[1]) * dy) / (len * len);
  return Math.max(0, Math.min(1, u));
}

function pointOnEdge(factor: Factor, t: number): [number, number] {
  const e = EDGES[factor];
  return [
    e.start[0] + t * (e.end[0] - e.start[0]),
    e.start[1] + t * (e.end[1] - e.start[1]),
  ];
}

export function randomPointInSector(factor: Factor): [number, number] {
  const [cx, cy] = sectorCentroid(factor);
  const x = cx + (Math.random() - 0.5) * 30;
  const y = cy + (Math.random() - 0.5) * 30;
  return clampToSector(x, y, factor);
}

export function resolveDropPosition(x: number, y: number): { factor: Factor; x: number; y: number } | null {
  let bestFactor = whichSector(x, y);
  if (!bestFactor) {
    let bestDist = Infinity;
    for (const factor of Object.keys(SECTORS) as Factor[]) {
      const t = projectToEdge(factor, x, y);
      const [px, py] = pointOnEdge(factor, t);
      const d = (px - x) ** 2 + (py - y) ** 2;
      if (d < bestDist) {
        bestDist = d;
        bestFactor = factor;
      }
    }
  }
  if (!bestFactor) return null;
  const [cx, cy] = clampToSector(x, y, bestFactor);
  return { factor: bestFactor, x: cx, y: cy };
}

/** Simple collision nudge for overlapping items (SVG coords). */
export function nudgeOverlappingItems(
  items: { id: string; factor: Factor; x: number; y: number }[]
): { id: string; x: number; y: number }[] {
  const MIN_DIST = 28;
  const updates = new Map<string, { x: number; y: number }>();
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i];
      const b = items[j];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < MIN_DIST && d > 0) {
        const nudge = (MIN_DIST - d) / 2;
        const nx = (dx / d) * nudge;
        const ny = (dy / d) * nudge;
        const ax = (updates.get(a.id)?.x ?? a.x) - nx;
        const ay = (updates.get(a.id)?.y ?? a.y) - ny;
        const bx = (updates.get(b.id)?.x ?? b.x) + nx;
        const by = (updates.get(b.id)?.y ?? b.y) + ny;
        const [cax, cay] = clampToSector(ax, ay, a.factor);
        const [cbx, cby] = clampToSector(bx, by, b.factor);
        updates.set(a.id, { x: cax, y: cay });
        updates.set(b.id, { x: cbx, y: cby });
      }
    }
  }
  return items.map((it) => {
    const u = updates.get(it.id);
    return u ? { id: it.id, x: u.x, y: u.y } : { id: it.id, x: it.x, y: it.y };
  });
}
